import {
  trackEvent,
  trackGA,
  trackCdp,
  trackCheckoutClick,
  trackViewFeature,
  trackClickFeature,
} from 'utilities/tracking';
import Sentry from 'application/exception_reporter';
import { getDefaultCurrencyCode } from 'utilities/currency';
import {
  getInitialProgressPercentageNumber,
  getProgressPercentByStep,
  getProgressPercentageNumber,
  isDashboardMode,
  mergeSearchParams,
} from 'helpers/search_parameter_helpers';
import {
  DASHBOARD_MODE_STEP_LEVELS,
  SEARCH_PARAM_NAMES,
} from 'constants/enums';

// Q: Why is there no step 4 nor the payment options + review steps?
// A: Over the years we have added and subtracted a lot of pages to the checkout
// flow and 4 just kind of got lost in the mix.
// A: The payment option and review steps do not exist in the old checkout
// flow, so we do not include them (for now).

// We intend to rebalance these steps once hardware analytics is ok with us doing so.
const CHECKOUT_STEP_MAP = {
  'Review Cart': 1,
  'Shipping Information': 2,
  'Shipping Method': 3,
  'Payment Option': 4,
  'Financing: Offer Select': 5,
  'Financing: Application': 6,
  'Payment Method': 7,
  'Review Order': 8,
  'Order Complete': 9,
};

const CHECKOUT_PAGE_CLICK_EVENTS = {
  financing_declined: 'Financing: Application Decline - Return to Checkout',
  financing_approved: 'Financing: Application Approved - Return to Checkout',
  financing_retry: 'Financing: Application Retry - Return to Form',
  financing_submitted: 'Financing: Application Submitted',
  offer_select_continue: 'Financing: Offer Select - Continue Button',
  offer_selected: 'Financing: Offer Select - Selected Offer',
  payment_form_submit: 'Checkout: Payment Form - Submit Button',
};

/**
 * Build checkout path with history search params as well as overrides
 * @param {*} history useHistory()
 * @param {*} path path for shop
 * @param {*} urlSearchParamsOverrides search params in key/value hash
 * @returns path string
 */
const buildCheckoutPath = (history, path, urlSearchParamsOverrides = {}) => {
  if (!path) return '';

  const originalUrlSearchParams = new URLSearchParams(
    history?.location?.search || '',
  );
  const newUrlSearchParams = new URLSearchParams();

  for (const [key, value] of Object.entries(urlSearchParamsOverrides)) {
    newUrlSearchParams.append(key, value);
  }

  const mergedSearchParams = mergeSearchParams(
    originalUrlSearchParams,
    newUrlSearchParams,
  );

  return `${path}${
    mergedSearchParams.toString() ? `?${mergedSearchParams.toString()}` : ''
  }`;
};

const getDashModeStepCompletionRatio = (path) => {
  const index = DASHBOARD_MODE_STEP_LEVELS.indexOf(path);
  return index === -1 ? 0 : (index + 1) / DASHBOARD_MODE_STEP_LEVELS.length;
};

const buildProgressSearchParamOverrides = (path) => {
  const progressPercent = getProgressPercentageNumber();
  let progressInitialPercent = getInitialProgressPercentageNumber();

  // If there the required no params, we should should try to override any params
  if (Number.isNaN(progressInitialPercent) && Number.isNaN(progressPercent)) {
    return {};
  }

  progressInitialPercent = progressInitialPercent || 0;

  const completionRatio = getDashModeStepCompletionRatio(path);
  const newProgressPercentage = getProgressPercentByStep(
    progressInitialPercent,
    completionRatio || 0,
  );

  return {
    [SEARCH_PARAM_NAMES.INITIAL_PROGRESS]: progressInitialPercent,
    [SEARCH_PARAM_NAMES.PROGRESS]: newProgressPercentage,
  };
};

const pushHistoryWithSearchParams = (
  history,
  path,
  urlSearchParamsOverrides = {},
  state = {},
) => {
  if (!path) return;

  history.push(
    buildCheckoutPath(history, path, {
      ...(isDashboardMode() ? buildProgressSearchParamOverrides(path) : {}),
      ...urlSearchParamsOverrides,
    }),
    state,
  );
};

const replaceHistoryWithSearchParams = (
  history,
  path,
  urlSearchParamsOverrides = {},
  state = {},
) => {
  if (!path) return;

  history.replace(
    buildCheckoutPath(history, path, {
      ...(isDashboardMode() ? buildProgressSearchParamOverrides(path) : {}),
      ...urlSearchParamsOverrides,
    }),
    state,
  );
};

const onCheckoutPageViewV2 = ({ componentName, additionalParameters }) => {
  trackViewFeature({
    componentName,
    eventDescription: `Viewed ${componentName}`,
    additionalParameters,
  });
};

const onCheckoutPageView = (name, trackingData) => {
  if (!trackingData) {
    return;
  }

  const index = CHECKOUT_STEP_MAP[name];
  if (index === undefined) {
    console.warn(`Invalid index for step ${name}!`);
  }

  const { order, products } = trackingData;

  products.forEach((p) => trackGA('ec:addProduct', p));
  trackGA('ec:setAction', 'checkout', { step: index });
  if (name === 'Order Complete') {
    trackGA('ec:setAction', 'purchase', order);
  }

  trackEvent({
    name: 'Checkout',
    value: `Step ${index}: ${name}`,
    params: order,
  });

  trackCdp({
    name: 'View Checkout Step',
    params: {
      feature: 'shop',
      step: name,
      ...trackingData,
    },
  });
};

const onCheckoutPageClick = ({
  eventDesc,
  trackingData,
  additionalDetails = {},
  componentName,
  actionItem,
  subActionItem,
}) => {
  if (!trackingData) {
    Sentry.capture(
      new Error(
        'No tracking data provided. Can not report onCheckoutPageClick',
      ),
    );

    return;
  }

  trackCdp({
    name: 'Complete Checkout Step',
    params: {
      feature: 'shop',
      step: eventDesc,
      ...trackingData,
    },
  });

  trackClickFeature({
    componentName,
    eventDescription: eventDesc,
    actionItem,
    subActionItem,
    additionalParameters: {
      ...additionalDetails,
      ...trackingData,
    },
  });

  trackCheckoutClick(eventDesc, trackingData.order, additionalDetails);
};

const onCheckoutComplete = (trackingData) => {
  const data = {
    order_id: trackingData.order.id,
    product_skus: [
      ...new Set(trackingData.products.map((product) => product.sku)),
    ].join(','),
    product_slugs: [
      ...new Set(trackingData.products.map((product) => product.slug)),
    ].join(','),
    state: 'complete',
    total: trackingData.order.revenue,
    // null-safe: falls back to the URL country's currency when the
    // sq:environment meta tag is absent (crawler-rendered pages)
    currency: getDefaultCurrencyCode(),
  };

  if (
    Sq.GoogleTagManager?.enabled &&
    (!Sq.OneTrust || Sq.OneTrust.advertisingTrackingEnabled)
  ) {
    Sq.GoogleTagManager.track(data);
  }

  if (!Sq.OneTrust || Sq.OneTrust.advertisingTrackingEnabled) {
    if (window.martech !== undefined) {
      window.martech.track({
        event: 'Shop Purchase',
        ...data,
      });
    }
  }
};

export {
  buildCheckoutPath,
  onCheckoutPageView,
  onCheckoutPageViewV2,
  onCheckoutPageClick,
  onCheckoutComplete,
  buildProgressSearchParamOverrides,
  pushHistoryWithSearchParams,
  replaceHistoryWithSearchParams,
  getDashModeStepCompletionRatio,
  CHECKOUT_PAGE_CLICK_EVENTS,
};
